import * as d3 from 'd3';
import React, { useEffect, useRef, useMemo, useState } from 'react';

import type { Band as LiqBand } from '@/features/liqMap/clusters';
import type { Level as SrLevel } from '@/features/srLevels/levels';
import { levelColor, levelLabel } from '@/features/srLevels/palette';
import { TokenIcon } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';
import type { ConnectionState } from '@/lib/ws';

import { createChartConfig, deepMerge, type ChartPreset } from '../config';
import type { ChartConfig, DeepPartial } from '../config/types';
import { useChartDimensions } from '../hooks/useChartDimensions';
import {
    usePriceConfigWithReset,
    type PriceConfig,
} from '../hooks/usePriceConfig';
import {
    renderTrendPath,
    renderCursor,
    renderAxis,
    renderPositionOverlay,
    createTwitchState,
    initSelectionsCache,
    type TwitchState,
    type SelectionsCache,
} from '../renderers';
import type { DataPoint, ActivePosition } from '../types';
import { generateInitialHistory } from '../utils/generateHistory';
import {
    calculatePhysicsStep,
    calculateSymmetricDomain,
    pruneHistory,
} from '../utils/physics';
import { computePnl, realtimePnl } from '../utils/pnl';
import { detectTrend } from '../utils/trendDetection';

import DevConfigPanel from './DevConfigPanel';
import TradeProgressBar from './TradeProgressBar';

// Trace buffer limits to keep long sessions bounded in memory/CPU.
const TRACE_POINTS_PER_PIXEL = 2;
const TRACE_MIN_POINTS = 120;
const TRACE_MAX_POINTS = 20000;

const getTraceMaxPoints = (
    chartWidth: number,
    rightMargin: number,
    viewportMs: number,
    lookbackMs: number,
): number => {
    const drawableWidth = Math.max(0, chartWidth - rightMargin);
    const widthBasedPoints = Math.ceil(drawableWidth * TRACE_POINTS_PER_PIXEL);
    const safeLookbackMs = Math.max(1, lookbackMs / 2);
    const lookbackPoints = Math.ceil(Math.max(1, viewportMs) / safeLookbackMs);

    return Math.min(
        TRACE_MAX_POINTS,
        Math.max(widthBasedPoints, TRACE_MIN_POINTS, lookbackPoints),
    );
};

interface TrendChartProps {
    /** Target price from market data */
    targetPrice: number;
    /** Active trading position (if any) */
    activePosition: ActivePosition | null;
    /** Callback when the simulated current value changes (throttled to ~15Hz) */
    onCurrentValueChange: (value: number, priceDiff: number) => void;
    /**
     * Preset configuration for quick "feel" changes.
     * Available presets: 'default', 'snappy', 'smooth', 'medicalMonitor', 'organic', 'aggressive'
     */
    preset?: ChartPreset;
    /**
     * Custom configuration overrides (applied after preset).
     * Use this for fine-tuning specific parameters.
     */
    config?: DeepPartial<ChartConfig>;
    /** Token pair to display in overlay (mobile) */
    tokenPair?: string;
    /** Current price to display in overlay (mobile) */
    displayPrice?: number;
    /** Price flash state for animation */
    priceFlash?: 'up' | 'down' | null;
    /** Connection state for overlay indicators */
    connectionState?: ConnectionState;
    /** Data is stale (no recent updates) */
    isStale?: boolean;
    /** Authoritative liquidation price for the active position (real mode). */
    liquidationPrice?: number | null;
    /**
     * Armed TP/SL + liquidation levels drawn as horizontal lines on the
     * live chart. Null = no position, so no level lines.
     */
    positionLevels?: {
        takeProfitPrice: number | null;
        stopLossPrice: number | null;
        liquidationPrice: number | null;
        /** a resting limit order's price; drawn even with no position open. */
        limitPrice?: number | null;
    } | null;
    /**
     * Liquidation clusters drawn as translucent horizontal bands behind the
     * line: lime below spot (longs force-closed there), red above (shorts).
     * Empty = nothing drawn. Same `Band`s the candles chart draws.
     */
    liqBands?: LiqBand[];
    /** Support/resistance levels, drawn as cyan bands. Empty = nothing drawn. */
    srLevels?: SrLevel[];
}

type LevelSel = d3.Selection<d3.BaseType, unknown, null, undefined>;

/** Rects per band layer; six a side is what bandsFor emits, four a side is S/R. */
const LIQ_BAND_POOL = 12;
const SR_LEVEL_POOL = 8;
const EMPTY_BANDS: LiqBand[] = [];
const EMPTY_LEVELS: SrLevel[] = [];
type BandRectSel = d3.Selection<SVGRectElement, unknown, null, undefined>;
type BandLineSel = d3.Selection<SVGLineElement, unknown, null, undefined>;
type BandTextSel = d3.Selection<SVGTextElement, unknown, null, undefined>;
/** Anything a band layer can draw: a price slice, how strongly, and optionally the level inside it. */
interface ChartBand {
    low: number;
    high: number;
    intensity: number;
    /** a crisp line at this price on top of the wash — S/R levels have one, liq bands do not */
    price?: number;
}
/** How a layer paints one band. */
interface BandStyle {
    color: string;
    fillOpacity: number;
    /** the level line, when the band has a price */
    line: { width: number; dashed: boolean } | null;
    label: string | null;
    labelAlign: 'start' | 'end';
}
/**
 * A pool of rects, lines and labels for one band layer, created once and
 * written only on change: static attributes per band VERSION, geometry per
 * frame for bands inside the window, show/hide tracked so a hidden element
 * is never re-hidden. This is what keeps a layer out of the 60 fps loop's way.
 */
interface BandLayer {
    group: d3.Selection<SVGGElement, unknown, null, undefined>;
    rects: BandRectSel[];
    lines: BandLineSel[];
    labels: BandTextSel[];
    version: number;
    ordered: ChartBand[];
    styles: BandStyle[];
    groupShown: boolean;
    rectShown: boolean[];
    lineShown: boolean[];
    labelShown: boolean[];
}

function createBandLayer(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    pool: number,
): BandLayer {
    const rects: BandRectSel[] = [];
    const lines: BandLineSel[] = [];
    const labels: BandTextSel[] = [];
    for (let i = 0; i < pool; i++) {
        rects.push(
            g
                .append('rect')
                .attr('x', 0)
                .attr('height', 0)
                .style('display', 'none'),
        );
        lines.push(g.append('line').attr('x1', 0).style('display', 'none'));
        labels.push(
            g
                .append('text')
                .attr('font-size', 9)
                .attr('font-family', 'monospace')
                .attr('font-weight', 'bold')
                .style('display', 'none'),
        );
    }
    return {
        group: g,
        rects,
        lines,
        labels,
        version: -1,
        ordered: [],
        styles: [],
        groupShown: false,
        rectShown: new Array<boolean>(pool).fill(false),
        lineShown: new Array<boolean>(pool).fill(false),
        labelShown: new Array<boolean>(pool).fill(false),
    };
}

function hideBand(L: BandLayer, i: number): void {
    if (L.rectShown[i]) {
        L.rects[i]!.style('display', 'none');
        L.rectShown[i] = false;
    }
    if (L.lineShown[i]) {
        L.lines[i]!.style('display', 'none');
        L.lineShown[i] = false;
    }
    if (L.labelShown[i]) {
        L.labels[i]!.style('display', 'none');
        L.labelShown[i] = false;
    }
}

/**
 * Paint one band layer for this frame. `version` changes when the data does
 * (at most once a minute); everything else is per-frame geometry for the
 * bands inside the window, which is usually none.
 */
function paintBandLayer<B extends ChartBand>(
    L: BandLayer,
    bands: readonly B[],
    version: number,
    yScale: (price: number) => number,
    width: number,
    height: number,
    style: (band: B, rank: number) => BandStyle,
): void {
    if (bands.length === 0) {
        if (L.groupShown) {
            L.group.style('display', 'none');
            L.groupShown = false;
        }
        return;
    }
    if (!L.groupShown) {
        L.group.style('display', 'block');
        L.groupShown = true;
    }
    const pool = L.rects.length;
    if (L.version !== version) {
        L.version = version;
        const ordered = [...bands]
            .sort((a, b) => b.intensity - a.intensity)
            .slice(0, pool);
        L.ordered = ordered;
        L.styles = ordered.map((band, rank) => style(band, rank));
        for (let i = 0; i < pool; i++) {
            const band = ordered[i];
            if (!band) {
                hideBand(L, i);
                continue;
            }
            const st = L.styles[i]!;
            L.rects[i]!.attr('fill', st.color).attr(
                'fill-opacity',
                st.fillOpacity,
            );
            if (st.line) {
                L.lines[i]!.attr('stroke', st.color)
                    .attr('stroke-width', st.line.width)
                    .attr('stroke-dasharray', st.line.dashed ? '2 3' : null)
                    .attr('stroke-opacity', 0.4 + 0.35 * band.intensity);
            }
            if (st.label !== null) {
                L.labels[i]!.attr('fill', st.color)
                    .attr('fill-opacity', 0.9)
                    .attr('text-anchor', st.labelAlign)
                    .text(st.label);
            }
        }
    }
    for (let i = 0; i < L.ordered.length; i++) {
        const band = L.ordered[i]!;
        const st = L.styles[i]!;
        const yTop = yScale(band.high);
        const yBottom = yScale(band.low);
        const inWindow =
            Number.isFinite(yTop) &&
            Number.isFinite(yBottom) &&
            yBottom >= 0 &&
            yTop <= height;
        if (!inWindow) {
            hideBand(L, i);
            continue;
        }
        const rect = L.rects[i]!;
        if (!L.rectShown[i]) {
            rect.style('display', 'block');
            L.rectShown[i] = true;
        }
        rect.attr('y', yTop)
            .attr('width', width)
            .attr('height', Math.max(2, yBottom - yTop));
        // the label sits on the level line when there is one, else on the band's top edge
        let labelY = yTop - 3;
        if (st.line && band.price !== undefined) {
            const yLine = yScale(band.price);
            const line = L.lines[i]!;
            if (!L.lineShown[i]) {
                line.style('display', 'block');
                L.lineShown[i] = true;
            }
            line.attr('x2', width).attr('y1', yLine).attr('y2', yLine);
            labelY = yLine - 4;
        }
        if (st.label !== null) {
            const label = L.labels[i]!;
            if (!L.labelShown[i]) {
                label.style('display', 'block');
                L.labelShown[i] = true;
            }
            label
                .attr('x', st.labelAlign === 'start' ? 6 : width - 6)
                .attr('y', labelY);
        } else if (L.labelShown[i]) {
            L.labels[i]!.style('display', 'none');
            L.labelShown[i] = false;
        }
    }
}

// local copy of the legend's formatter: this file must not import a React
// feature module into its 60fps render path
function formatLiqNotional(usd: number): string {
    if (usd >= 1e9) return `$${(usd / 1e9).toFixed(1)}B`;
    if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
    if (usd >= 1e3) return `$${(usd / 1e3).toFixed(0)}K`;
    return `$${Math.round(usd)}`;
}
interface LevelSelections {
    group: LevelSel;
    tp: { line: LevelSel; text: LevelSel };
    sl: { line: LevelSel; text: LevelSel };
    liq: { line: LevelSel; text: LevelSel };
    /** a RESTING limit order's price — drawn with no position on the chart. */
    limit: { line: LevelSel; text: LevelSel };
}

/** Neutral, not lime: a resting order is a marker, not action or profit. */
const LIMIT_LEVEL_COLOR = '#98A08A';

interface PipNodes {
    group: SVGGElement;
    sideG: SVGGElement;
    sideBg: SVGRectElement;
    sideText: SVGTextElement;
    priceBg: SVGRectElement;
    priceText: SVGTextElement;
    pctText: SVGTextElement;
    usdBg: SVGRectElement;
    usdText: SVGTextElement;
}

const TrendChart: React.FC<TrendChartProps> = ({
    targetPrice,
    activePosition,
    onCurrentValueChange,
    preset = 'smooth',
    config: configOverrides,
    tokenPair,
    displayPrice,
    priceFlash,
    connectionState = 'connected',
    isStale = false,
    liquidationPrice,
    positionLevels = null,
    liqBands = EMPTY_BANDS,
    srLevels = EMPTY_LEVELS,
}) => {
    // Base coin for the mobile pair badge, e.g. "BTC/USD" -> "btc".
    const baseToken =
        (tokenPair ?? '').split(/[/-]/)[0]?.toLowerCase() || 'usd';
    // Dev mode state for config panel
    const [devPreset, setDevPreset] = useState<ChartPreset | undefined>(preset);
    const [devOverrides, setDevOverrides] = useState<DeepPartial<ChartConfig>>(
        {},
    );

    // Resolve configuration from preset and overrides
    // In dev mode, dev panel settings take precedence
    const chartConfig = useMemo(() => {
        const baseConfig = createChartConfig(
            devPreset ?? preset,
            configOverrides,
        );
        return import.meta.env.DEV
            ? deepMerge(baseConfig, devOverrides)
            : baseConfig;
    }, [preset, devPreset, configOverrides, devOverrides]);

    // Reset dev config handler
    const handleDevReset = () => {
        setDevPreset(preset);
        setDevOverrides({});
    };

    // Price-adaptive configuration for formatting and scaling
    const { priceConfig, hasConfigChanged } =
        usePriceConfigWithReset(targetPrice);

    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { width, height } = useChartDimensions(containerRef);

    const animationFrameId = useRef<number | null>(null);

    // Throttle callback to ~15Hz to avoid React state updates at 60fps
    const lastReportTimeRef = useRef(0);
    const lastReportedValueRef = useRef(targetPrice);
    const REPORT_INTERVAL_MS = 66; // ~15fps for React state updates

    // Local price for TradeProgressBar, updated at the same ~15Hz throttle
    const [chartPrice, setChartPrice] = useState(targetPrice);

    // --- Mutable Simulation State ---
    const simulationRef = useRef({
        currentVal: targetPrice,
        velocity: 0,
        lastFrameTime: 0,
        // monotonic "chart clock" (ms) that advances only by rendered frames
        chartTime: 0,
        renderedMin: targetPrice - priceConfig.minSpread,
        renderedMax: targetPrice + priceConfig.minSpread,
    });

    const traceRef = useRef<DataPoint[]>([]);
    const lastTraceTimeRef = useRef(0);
    const targetPriceRef = useRef(targetPrice);
    const activePosRef = useRef(activePosition);
    const configRef = useRef(chartConfig);
    const priceConfigRef = useRef<PriceConfig>(priceConfig);
    // Armed TP/SL/liq level lines
    const positionLevelsRef = useRef(positionLevels);
    const liqBandsRef = useRef<LiqBand[]>(liqBands);
    // bumped whenever the bands change, so the render loop repaints static
    // attributes once per change instead of once per frame
    const liqBandsVersionRef = useRef(0);
    // the band rects are created once and re-positioned per frame, so the
    // render loop never allocates DOM for them
    const liqLayerRef = useRef<BandLayer | null>(null);
    const srLevelsRef = useRef<SrLevel[]>(srLevels);
    const srLevelsVersionRef = useRef(0);
    const srLayerRef = useRef<BandLayer | null>(null);

    // Twitch animation state for cursor (avoids module-level mutable state)
    const twitchRef = useRef<TwitchState>(createTwitchState(targetPrice));

    // Cached D3 selections to avoid DOM queries every frame
    const selectionsRef = useRef<SelectionsCache | null>(null);

    // SVG price+PnL pip. Skeleton is declared in JSX once and mutated
    // imperatively in the render loop (like the cursor label), so it lives
    // in the chart's repaint and moving it is free.
    const pipRef = useRef<SVGGElement>(null);
    const pipNodesRef = useRef<PipNodes | null>(null);
    const pipStateRef = useRef({ key: '', w: 0, h: 0 });
    // Cached text widths so layoutPip stays write-only on the hot path.
    // getComputedTextLength forces a synchronous SVG layout flush mid-rAF
    // (the sim price micro-moves every frame, so layoutPip runs ~60Hz);
    // widths only change when the string gets longer/shorter (price/pct/usd
    // are numeric — Geist Mono is monospace, Archivo digits are uniform to
    // ~1px), so same-length strings reuse the cached measurement. The side
    // chip ("LONG 25x") is proportional text and caches per exact string.
    const pipMeasureRef = useRef<{
        side: { str: string; w: number } | null;
        price: { len: number; w: number } | null;
        pct: { len: number; w: number } | null;
        usd: { len: number; w: number } | null;
    }>({ side: null, price: null, pct: null, usd: null });
    // Cached D3 selections for the TP/SL/liq level lines (avoids a
    // querySelector storm every frame while a position is open).
    const levelSelRef = useRef<LevelSelections | null>(null);
    // Cache the pip's child nodes once (avoids querySelector per frame).
    useEffect(() => {
        const g = pipRef.current;
        if (!g) {
            pipNodesRef.current = null;
            return;
        }
        const q = <T extends Element>(sel: string): T =>
            g.querySelector(sel) as T;
        pipNodesRef.current = {
            group: g,
            sideG: q('.pip-side'),
            sideBg: q('.pip-side-bg'),
            sideText: q('.pip-side-text'),
            priceBg: q('.pip-price-bg'),
            priceText: q('.pip-price-text'),
            pctText: q('.pip-pct-text'),
            usdBg: q('.pip-usd-bg'),
            usdText: q('.pip-usd-text'),
        };
        pipMeasureRef.current = {
            side: null,
            price: null,
            pct: null,
            usd: null,
        };
        pipStateRef.current.key = ''; // force a relayout
    }, []);

    // Widths measured before the webfonts (Geist Mono / Archivo) arrive are
    // fallback-font widths; drop the cache once fonts are ready so the next
    // frame re-measures with the real metrics.
    useEffect(() => {
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (cancelled) return;
            pipMeasureRef.current = {
                side: null,
                price: null,
                pct: null,
                usd: null,
            };
            pipStateRef.current.key = '';
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // Measure-with-cache helpers for layoutPip (see pipMeasureRef).
    const measurePipNumeric = (
        node: SVGTextElement,
        slot: 'price' | 'pct' | 'usd',
        str: string,
    ): number => {
        const hit = pipMeasureRef.current[slot];
        if (hit && hit.len === str.length) return hit.w;
        const w = node.getComputedTextLength();
        pipMeasureRef.current[slot] = { len: str.length, w };
        return w;
    };
    const measurePipSide = (node: SVGTextElement, str: string): number => {
        const hit = pipMeasureRef.current.side;
        if (hit && hit.str === str) return hit.w;
        const w = node.getComputedTextLength();
        pipMeasureRef.current.side = { str, w };
        return w;
    };

    // Lay out the pip pieces. Runs on every content change — which, with
    // the physics jitter, is nearly every frame — so it must stay
    // write-only: text widths come from the pipMeasureRef cache and are
    // only re-measured (a forced synchronous SVG layout) when a string's
    // length changes.
    const layoutPip = (
        hasPos: boolean,
        type: 'LONG' | 'SHORT',
        priceStr: string,
        sideStr: string,
        pctStr: string,
        usdStr: string,
        winning: boolean,
    ) => {
        const n = pipNodesRef.current;
        if (!n) return;
        const LIME = '#d3ff12';
        const PINK = '#ff2d64';
        const col = winning ? LIME : PINK;
        const sideCol = type === 'LONG' ? LIME : PINK;
        const sideInk = type === 'LONG' ? '#0c0e00' : '#12030a';
        const usdInk = winning ? '#0c0e00' : '#12030a';
        let cy = 0;
        let maxW = 0;

        if (hasPos) {
            n.sideG.style.display = '';
            n.sideText.textContent = sideStr;
            n.sideText.setAttribute('fill', sideInk);
            n.sideText.setAttribute('x', '8');
            n.sideText.setAttribute('y', String(cy + 12.5));
            const bw = measurePipSide(n.sideText, sideStr) + 16;
            n.sideBg.setAttribute('x', '0');
            n.sideBg.setAttribute('y', String(cy));
            n.sideBg.setAttribute('width', String(bw));
            n.sideBg.setAttribute('height', '17');
            n.sideBg.setAttribute('fill', sideCol);
            maxW = Math.max(maxW, bw);
            cy += 21;
        } else {
            n.sideG.style.display = 'none';
        }

        n.priceText.textContent = priceStr;
        n.priceText.setAttribute('fill', col);
        n.priceText.setAttribute('x', '9');
        n.priceText.setAttribute('y', String(cy + 15));
        const pbw = measurePipNumeric(n.priceText, 'price', priceStr) + 18;
        n.priceBg.setAttribute('x', '0');
        n.priceBg.setAttribute('y', String(cy));
        n.priceBg.setAttribute('width', String(pbw));
        n.priceBg.setAttribute('height', '22');
        n.priceBg.setAttribute('stroke', col);
        maxW = Math.max(maxW, pbw);
        cy += 22;

        if (hasPos) {
            cy += 5;
            n.pctText.style.display = '';
            n.pctText.textContent = pctStr;
            n.pctText.setAttribute('fill', col);
            n.pctText.setAttribute('x', '2');
            n.pctText.setAttribute('y', String(cy + 17));
            maxW = Math.max(
                maxW,
                measurePipNumeric(n.pctText, 'pct', pctStr) + 2,
            );
            cy += 23;

            n.usdBg.style.display = '';
            n.usdText.style.display = '';
            n.usdText.textContent = usdStr;
            n.usdText.setAttribute('fill', usdInk);
            n.usdText.setAttribute('x', '8');
            n.usdText.setAttribute('y', String(cy + 13));
            const ubw = measurePipNumeric(n.usdText, 'usd', usdStr) + 16;
            n.usdBg.setAttribute('x', '0');
            n.usdBg.setAttribute('y', String(cy));
            n.usdBg.setAttribute('width', String(ubw));
            n.usdBg.setAttribute('height', '18');
            n.usdBg.setAttribute('fill', col);
            maxW = Math.max(maxW, ubw);
            cy += 18;
        } else {
            n.pctText.style.display = 'none';
            n.usdBg.style.display = 'none';
            n.usdText.style.display = 'none';
        }

        pipStateRef.current.w = maxW;
        pipStateRef.current.h = cy;
    };

    // Sync props to refs
    useEffect(() => {
        targetPriceRef.current = targetPrice;
    }, [targetPrice]);
    useEffect(() => {
        activePosRef.current = activePosition;
    }, [activePosition]);
    useEffect(() => {
        positionLevelsRef.current = positionLevels;
    }, [positionLevels]);
    useEffect(() => {
        liqBandsRef.current = liqBands;
        liqBandsVersionRef.current += 1;
    }, [liqBands]);
    useEffect(() => {
        srLevelsRef.current = srLevels;
        srLevelsVersionRef.current += 1;
    }, [srLevels]);
    useEffect(() => {
        configRef.current = chartConfig;
    }, [chartConfig]);
    useEffect(() => {
        priceConfigRef.current = priceConfig;
    }, [priceConfig]);

    // Reset simulation state when price magnitude changes significantly
    // (e.g., switching from BTC at $90,000 to CRO at $0.09)
    useEffect(() => {
        if (hasConfigChanged) {
            const sim = simulationRef.current;
            const config = configRef.current;

            sim.currentVal = targetPrice;
            sim.velocity = 0;
            sim.renderedMin = targetPrice - priceConfig.minSpread;
            sim.renderedMax = targetPrice + priceConfig.minSpread;

            // Regenerate historical data with new price scale
            const nowChart = sim.chartTime || performance.now();
            traceRef.current = generateInitialHistory(
                targetPrice,
                config.viewportMs,
                undefined,
                nowChart,
            );
            lastTraceTimeRef.current = nowChart;

            // Reset twitch state to avoid stale animation
            twitchRef.current = createTwitchState(targetPrice);
        }
    }, [hasConfigChanged, targetPrice, priceConfig.minSpread]);

    // Initialize D3 selections cache after SVG mounts
    // This avoids querying the DOM every frame in the animation loop
    useEffect(() => {
        if (svgRef.current && width > 0 && height > 0) {
            const svg = d3.select(svgRef.current);
            selectionsRef.current = initSelectionsCache(svg);
        }
    }, [width, height]);

    // Dimensions and the report callback are read through refs so the rAF
    // loop below can run for the component's whole life. Re-running the loop
    // effect on resize used to regenerate the trace history and reset the
    // chart clock — every ResizeObserver tick (window drag, the trade
    // terminal's 250ms height animation) visibly rebuilt the chart.
    const dimensionsRef = useRef({ width, height });
    useEffect(() => {
        dimensionsRef.current = { width, height };
    }, [width, height]);
    const onCurrentValueChangeRef = useRef(onCurrentValueChange);
    useEffect(() => {
        onCurrentValueChangeRef.current = onCurrentValueChange;
    }, [onCurrentValueChange]);

    useEffect(() => {
        // Initialize simulation state with a few pre-filled historical data points
        const now = performance.now();
        const config = configRef.current;

        // Generate a small set of historical data points close to the current price
        traceRef.current = generateInitialHistory(
            targetPriceRef.current,
            config.viewportMs,
            undefined,
            now,
        );

        simulationRef.current.lastFrameTime = now;
        simulationRef.current.chartTime = now;
        lastTraceTimeRef.current = now;

        const loop = (rafTime: number) => {
            const { width, height } = dimensionsRef.current;
            if (!svgRef.current || width === 0 || height === 0) {
                animationFrameId.current = requestAnimationFrame(loop);
                return;
            }

            // Get current config from ref (allows hot-swapping)
            const config = configRef.current;
            const priceCfg = priceConfigRef.current;

            const sim = simulationRef.current;
            const dt = Math.min((rafTime - sim.lastFrameTime) / 1000, 0.05); // Cap delta time
            sim.lastFrameTime = rafTime;
            // Advance the monotonic chart clock by the rendered (clamped) frame
            // only. When rAF is paused — hidden tab, sleep, alt-tab — it stops
            // instead of jumping forward with wall time, so the trace isn't
            // aged past the viewport and wiped by pruneHistory on return; the
            // chart resumes exactly where it left off.
            sim.chartTime += dt * 1000;
            const time = sim.chartTime;

            // 1. Physics Step (with injected config)
            const nextPhysics = calculatePhysicsStep(
                { currentVal: sim.currentVal, velocity: sim.velocity },
                targetPriceRef.current,
                dt,
                time,
                config.physics,
                priceConfigRef.current.minSpread,
            );
            sim.currentVal = nextPhysics.currentVal;
            sim.velocity = nextPhysics.velocity;

            // 2. Data Management (sample + cap to keep memory bounded)
            const effectiveViewportMs = Math.max(1, config.viewportMs);
            const maxTracePoints = getTraceMaxPoints(
                width,
                config.rightMargin,
                effectiveViewportMs,
                config.trendDetection.lookbackMs,
            );
            // Sampling to avoid storing more points than the screen can render.
            const sampleIntervalMs = effectiveViewportMs / maxTracePoints;
            const shouldAppend =
                time - lastTraceTimeRef.current >= sampleIntervalMs;

            if (shouldAppend) {
                traceRef.current.push({
                    timestamp: time,
                    value: sim.currentVal,
                });
                lastTraceTimeRef.current = time;
            } else {
                const lastPoint = traceRef.current[traceRef.current.length - 1];
                if (lastPoint) {
                    lastPoint.timestamp = time;
                    lastPoint.value = sim.currentVal;
                } else {
                    traceRef.current.push({
                        timestamp: time,
                        value: sim.currentVal,
                    });
                    lastTraceTimeRef.current = time;
                }
            }

            pruneHistory(
                traceRef.current,
                time,
                effectiveViewportMs,
                maxTracePoints,
            );

            // 3. Camera / Domain Calculation (with injected scaling config and price-scaled minSpread)
            const [nextMin, nextMax] = calculateSymmetricDomain(
                sim.currentVal,
                traceRef.current,
                sim.renderedMin,
                sim.renderedMax,
                config.scaling,
                priceCfg.minSpread, // Price-scaled override
            );
            sim.renderedMin = nextMin;
            sim.renderedMax = nextMax;

            // 4. Rendering
            const svg = d3.select(svgRef.current);

            const yScale = d3
                .scaleLinear()
                .domain([sim.renderedMin, sim.renderedMax])
                .range([height, 0]);

            const pixelsPerMs =
                (width - config.rightMargin) / config.viewportMs;
            const getX = (t: number) =>
                width - config.rightMargin - (time - t) * pixelsPerMs;

            // Trend Detection for Color (with injected config and price-scaled thresholds)
            const { color: trendColor } = detectTrend(
                sim.currentVal,
                traceRef.current,
                time,
                config.trendDetection,
                config.theme,
                {
                    bullishThreshold: priceCfg.bullishThreshold,
                    bearishThreshold: priceCfg.bearishThreshold,
                },
            );

            // Position-relative coloring: when a position is active, color based on
            // whether the position is in profit or loss
            let finalColor = trendColor;
            const pos = activePosRef.current;
            if (pos) {
                // LONG profits when price is above entry, SHORT profits when below
                const isProfit =
                    pos.type === 'LONG'
                        ? sim.currentVal >= pos.entryPrice
                        : sim.currentVal <= pos.entryPrice;
                finalColor = isProfit
                    ? config.theme.profit
                    : config.theme.bearish;
            }

            // Get cached selections (may be null on first frame before cache is initialized)
            const cache = selectionsRef.current ?? undefined;

            // Draw Sub-components (with injected theme/layout/price config and cached selections)
            renderTrendPath(
                svg,
                traceRef.current,
                getX,
                yScale,
                height,
                finalColor,
                cache,
            );
            const cursorX = getX(time);
            const cursorY = yScale(sim.currentVal);
            renderCursor(
                svg,
                cursorX,
                cursorY,
                sim.currentVal,
                width,
                height,
                finalColor,
                config.layout,
                priceCfg, // Price formatting config
                twitchRef.current, // Twitch animation state
                cache,
            );
            // Position the SVG pip at the line tip. Content is re-laid-out
            // only when it changes; the per-frame work is one transform
            // attribute — inside the chart's repaint.
            if (pipNodesRef.current && sim.currentVal > 0) {
                const n = pipNodesRef.current;
                n.group.style.display = '';
                const pos = activePosRef.current;
                const pc = priceConfigRef.current;
                const priceStr = `$${pc.formatDisplay(sim.currentVal)}`;
                let sideStr = '';
                let pctStr = '';
                let usdStr = '';
                let winning = true;
                const type = pos?.type ?? 'LONG';
                if (pos) {
                    // The exchange's own figures when we have them. Recomputing
                    // here cannot reproduce them even from correct inputs:
                    // Hyperliquid prices PnL off MARK, and sim.currentVal is the
                    // animated LAST price this chart is drawing. Falling back is
                    // for demo mode and the gap before the first position read.
                    const exchangePnl = pos.pnlValue ?? null;
                    const { pnlValue, pnlPercent } =
                        exchangePnl !== null
                            ? realtimePnl(
                                  {
                                      pnlValue: exchangePnl,
                                      pnlPercent: pos.pnlPercent ?? null,
                                      anchorPrice: pos.pnlAnchorPrice ?? null,
                                      size: pos.size ?? null,
                                      isLong: pos.type === 'LONG',
                                      marginUsed: pos.amount,
                                  },
                                  sim.currentVal,
                              )
                            : computePnl(
                                  pos.entryPrice,
                                  sim.currentVal,
                                  pos.type,
                                  pos.amount,
                                  pos.leverage,
                              );
                    winning = pnlValue >= 0;
                    const s = winning ? '+' : '';
                    sideStr = `${pos.type} ${pos.leverage}x`;
                    pctStr = `${s}${pnlPercent.toFixed(2)}%`;
                    usdStr = `${s}$${pnlValue.toFixed(2)}`;
                }
                const key = `${priceStr}|${sideStr}|${pctStr}|${usdStr}|${winning}`;
                if (key !== pipStateRef.current.key) {
                    pipStateRef.current.key = key;
                    layoutPip(
                        pos !== null,
                        type,
                        priceStr,
                        sideStr,
                        pctStr,
                        usdStr,
                        winning,
                    );
                }
                const dims = pipStateRef.current;
                const gap = 14;
                const fitsRight = cursorX + gap + dims.w <= width - 6;
                const px = Math.min(
                    Math.max(
                        6,
                        fitsRight ? cursorX + gap : cursorX - gap - dims.w,
                    ),
                    Math.max(6, width - dims.w - 6),
                );
                const py = Math.min(
                    Math.max(6, cursorY - dims.h / 2),
                    Math.max(6, height - dims.h - 6),
                );
                n.group.setAttribute('transform', `translate(${px}, ${py})`);
            } else if (pipNodesRef.current) {
                pipNodesRef.current.group.style.display = 'none';
            }
            renderAxis(
                svg,
                yScale,
                width,
                config.theme,
                config.layout,
                priceCfg,
                cache,
            );

            // Band layers, behind the position overlay and the line: context,
            // never in front of the price. Each is a pool of rects created
            // once and written only on change (see paintBandLayer).
            {
                if (!liqLayerRef.current) {
                    liqLayerRef.current = createBandLayer(
                        svg.select<SVGGElement>('.liq-bands'),
                        LIQ_BAND_POOL,
                    );
                }
                if (!srLayerRef.current) {
                    srLayerRef.current = createBandLayer(
                        svg.select<SVGGElement>('.sr-levels'),
                        SR_LEVEL_POOL,
                    );
                }
                let labelled = 0;
                paintBandLayer(
                    liqLayerRef.current,
                    liqBandsRef.current,
                    liqBandsVersionRef.current,
                    yScale,
                    width,
                    height,
                    (band) => {
                        // label only the two strongest per side; more is clutter
                        const label =
                            labelled < 4 && band.intensity > 0.5
                                ? formatLiqNotional(band.notionalUsd)
                                : null;
                        if (label !== null) labelled++;
                        return {
                            color:
                                band.side === 'long'
                                    ? config.theme.profit
                                    : config.theme.bearish,
                            fillOpacity: 0.06 + 0.26 * band.intensity,
                            line: null,
                            label,
                            labelAlign: 'end',
                        };
                    },
                );
                paintBandLayer(
                    srLayerRef.current,
                    srLevelsRef.current,
                    srLevelsVersionRef.current,
                    yScale,
                    width,
                    height,
                    (level) => ({
                        color: levelColor(level.kind),
                        // a wash between the swings that make the level; the line is the level
                        fillOpacity: 0.04 + 0.06 * level.intensity,
                        // quiet dotted 1px on both sides; strength lives in the label
                        line: { width: 1, dashed: true },
                        label: levelLabel(level),
                        labelAlign: 'start',
                    }),
                );
            }

            // Draw Trading Overlay
            renderPositionOverlay(
                svg,
                activePosRef.current,
                sim.currentVal,
                yScale,
                width,
                getX(time),
                config.theme,
                config.layout,
                cache,
            );

            // Armed TP/SL + liquidation lines. Hidden entirely when
            // positionLevels is null or no position is open.
            {
                // Cache the level-line selections once (the group is always
                // in the DOM) so this per-frame block does zero querySelector.
                if (!levelSelRef.current) {
                    const g = svg.select('.position-levels');
                    const mk = (cls: string) => ({
                        line: g.select(`.level-${cls}`),
                        text: g.select(`.level-${cls}-label`),
                    });
                    levelSelRef.current = {
                        group: g,
                        tp: mk('tp'),
                        sl: mk('sl'),
                        liq: mk('liq'),
                        limit: mk('limit'),
                    };
                }
                const L = levelSelRef.current;
                const levels = positionLevelsRef.current;
                const limitPrice = levels?.limitPrice ?? null;
                // A resting limit order draws its line with NO position open —
                // that is the whole point of it, so the group cannot stay gated
                // on a position the way the TP/SL/liq lines are.
                if (!levels || (!activePosRef.current && limitPrice === null)) {
                    L.group.style('display', 'none');
                } else {
                    L.group.style('display', 'block');
                    const drawLevel = (
                        sel: { line: LevelSel; text: LevelSel },
                        price: number | null,
                        color: string,
                        label: string,
                    ) => {
                        if (price === null || price <= 0) {
                            sel.line.style('display', 'none');
                            sel.text.style('display', 'none');
                            return;
                        }
                        const y = yScale(price);
                        if (!Number.isFinite(y)) {
                            sel.line.style('display', 'none');
                            sel.text.style('display', 'none');
                            return;
                        }
                        sel.line
                            .style('display', 'block')
                            .attr('x1', 0)
                            .attr('x2', width)
                            .attr('y1', y)
                            .attr('y2', y)
                            .attr('stroke', color);
                        sel.text
                            .style('display', 'block')
                            .attr('x', 8)
                            .attr('y', y - 5)
                            .attr('fill', color)
                            .text(label);
                    };
                    drawLevel(
                        L.tp,
                        levels.takeProfitPrice,
                        config.theme.profit,
                        'TP',
                    );
                    drawLevel(
                        L.sl,
                        levels.stopLossPrice,
                        config.theme.bearish,
                        'SL',
                    );
                    drawLevel(
                        L.liq,
                        levels.liquidationPrice,
                        config.theme.bearish,
                        'LIQ',
                    );
                    drawLevel(L.limit, limitPrice, LIMIT_LEVEL_COLOR, 'LIMIT');
                }
            }

            // 5. Output to Parent (throttled to ~15Hz to avoid excessive React re-renders)
            if (time - lastReportTimeRef.current > REPORT_INTERVAL_MS) {
                const priceDiff = sim.currentVal - lastReportedValueRef.current;
                onCurrentValueChangeRef.current(sim.currentVal, priceDiff);
                setChartPrice(sim.currentVal);
                lastReportedValueRef.current = sim.currentVal;
                lastReportTimeRef.current = time;
            }

            animationFrameId.current = requestAnimationFrame(loop);
        };

        animationFrameId.current = requestAnimationFrame(loop);
        return () => {
            if (animationFrameId.current)
                cancelAnimationFrame(animationFrameId.current);
        };
        // Mount-once: dimensions + report callback flow through refs above, so
        // resizes never reset the trace or the chart clock.
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative h-full w-full overflow-hidden bg-card"
        >
            <svg
                ref={svgRef}
                role="img"
                aria-label={`Live price chart for ${tokenPair}`}
                className="block h-full w-full"
            >
                <defs>
                    {/* Gradient for area fill under the line */}
                    <linearGradient
                        id="line-gradient"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <stop offset="0%" stopOpacity="0.25" />
                        <stop offset="100%" stopOpacity="0" />
                    </linearGradient>

                    {/* Glow filter - two-radius bloom, still single element.
                        Tight halo keeps the line crisp; the wide pass adds the
                        phosphor-like ambient bloom that makes the lime read as
                        emitted light on the darkest surface. */}
                    <filter
                        id="line-glow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                        colorInterpolationFilters="sRGB"
                    >
                        <feGaussianBlur
                            in="SourceGraphic"
                            stdDeviation="3"
                            result="blur"
                        />
                        <feGaussianBlur
                            in="SourceGraphic"
                            stdDeviation="9"
                            result="bloom"
                        />
                        <feComponentTransfer in="bloom" result="bloomSoft">
                            <feFuncA type="linear" slope="0.55" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode in="bloomSoft" />
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Reticle glow - optimized for small cursor element */}
                    {/* Reduced box from 500% to 300%, added sRGB mode */}
                    <filter
                        id="reticle-glow"
                        x="-100%"
                        y="-100%"
                        width="300%"
                        height="300%"
                        colorInterpolationFilters="sRGB"
                    >
                        <feGaussianBlur
                            in="SourceGraphic"
                            stdDeviation="3"
                            result="blur1"
                        />
                        <feGaussianBlur
                            in="SourceGraphic"
                            stdDeviation="1"
                            result="blur2"
                        />
                        <feMerge>
                            <feMergeNode in="blur1" />
                            <feMergeNode in="blur2" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <rect width="100%" height="100%" fill="url(#grid-pattern)" />
                <pattern
                    id="grid-pattern"
                    width="40"
                    height="40"
                    patternUnits="userSpaceOnUse"
                >
                    <path
                        d="M 40 0 L 0 0 0 40"
                        fill="none"
                        stroke={chartConfig.theme.grid}
                        strokeWidth="1"
                    />
                </pattern>

                <g className="y-axis-labels" />

                {/* Band layers — populated once by the render loop */}
                <g className="liq-bands" style={{ display: 'none' }} />
                <g className="sr-levels" style={{ display: 'none' }} />

                {/* Trading Overlay Group */}
                <g className="position-overlay" style={{ display: 'none' }}>
                    <line
                        className="entry-line"
                        strokeDasharray="6 4"
                        strokeWidth="1"
                    />
                    <g className="pnl-badge">
                        <rect
                            x="0"
                            y="-10"
                            width="80"
                            height="20"
                            rx="4"
                            strokeWidth="1"
                        />
                        <text
                            x="40"
                            y="1"
                            textAnchor="middle"
                            alignmentBaseline="middle"
                            fontSize="11"
                            fontFamily="monospace"
                            fontWeight="bold"
                        ></text>
                    </g>
                </g>

                {/* Armed TP/SL/liq level lines */}
                <g className="position-levels" style={{ display: 'none' }}>
                    <line
                        className="level-tp"
                        strokeDasharray="5 5"
                        strokeWidth="1"
                        opacity="0.7"
                    />
                    <text
                        className="level-tp-label"
                        fontSize="9"
                        fontFamily="monospace"
                        fontWeight="bold"
                        opacity="0.9"
                    />
                    <line
                        className="level-sl"
                        strokeDasharray="5 5"
                        strokeWidth="1"
                        opacity="0.7"
                    />
                    <text
                        className="level-sl-label"
                        fontSize="9"
                        fontFamily="monospace"
                        fontWeight="bold"
                        opacity="0.9"
                    />
                    <line
                        className="level-liq"
                        strokeWidth="1.5"
                        opacity="0.8"
                    />
                    <text
                        className="level-liq-label"
                        fontSize="9"
                        fontFamily="monospace"
                        fontWeight="bold"
                        opacity="0.9"
                    />
                    <line
                        className="level-limit"
                        strokeDasharray="5 5"
                        strokeWidth="1"
                        opacity="0.7"
                    />
                    <text
                        className="level-limit-label"
                        fontSize="9"
                        fontFamily="monospace"
                        fontWeight="bold"
                        opacity="0.9"
                    />
                </g>

                {/* Area fill with gradient */}
                <path className="area-path" fill="url(#line-gradient)" />

                {/* Static Elements (Crosshairs) */}
                <line
                    className="cursor-line"
                    stroke="#ffffff"
                    strokeOpacity="0.1"
                    strokeDasharray="4 4"
                />
                <line
                    className="cursor-vert-line"
                    stroke="#ffffff"
                    strokeOpacity="0.1"
                    strokeDasharray="4 4"
                />

                {/* Outer glow path */}
                <path
                    className="line-path"
                    fill="none"
                    strokeWidth="3"
                    filter="url(#line-glow)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Inner core path - white hot center */}
                <path
                    className="line-path-core"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Reticle cursor - bracket frame around current price point */}
                <g className="cursor-reticle" filter="url(#reticle-glow)">
                    {/* Central bright core */}
                    <circle className="cursor-core" r="2" fill="white" />
                    {/* Corner brackets - [ ] frame */}
                    <path
                        className="bracket-tl"
                        fill="none"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                    />
                    <path
                        className="bracket-tr"
                        fill="none"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                    />
                    <path
                        className="bracket-bl"
                        fill="none"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                    />
                    <path
                        className="bracket-br"
                        fill="none"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                    />
                </g>

                <g className="cursor-label" style={{ pointerEvents: 'none' }}>
                    <rect
                        x="0"
                        y="-12"
                        width="64"
                        height="24"
                        rx="4"
                        fill="#09090b"
                        strokeOpacity="0.8"
                    />
                    <text
                        x="32"
                        y="1"
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fontSize="12"
                        fontFamily="monospace"
                        fontWeight="bold"
                    >
                        0
                    </text>
                </g>

                {/* SVG price+PnL pip. Skeleton only — text/size/position
                    are set imperatively in the render loop (layoutPip +
                    per-frame transform). Candles mode uses the HTML overlay
                    pip instead. */}
                <g
                    ref={pipRef}
                    className="pnl-pip"
                    style={{ pointerEvents: 'none', display: 'none' }}
                >
                    <g className="pip-side">
                        <rect className="pip-side-bg" rx="3" />
                        <text
                            className="pip-side-text"
                            fontFamily="'Archivo', sans-serif"
                            fontStyle="italic"
                            fontWeight="800"
                            fontSize="10.5"
                        />
                    </g>
                    <rect
                        className="pip-price-bg"
                        rx="6"
                        fill="rgba(6,7,4,0.92)"
                        strokeWidth="1"
                    />
                    <text
                        className="pip-price-text"
                        fontFamily="'Geist Mono', monospace"
                        fontWeight="700"
                        fontSize="12.5"
                    />
                    <text
                        className="pip-pct-text"
                        fontFamily="'Archivo', sans-serif"
                        fontStyle="italic"
                        fontWeight="900"
                        fontSize="20"
                    />
                    <rect className="pip-usd-bg" rx="9" />
                    <text
                        className="pip-usd-text"
                        fontFamily="'Geist Mono', monospace"
                        fontWeight="700"
                        fontSize="11"
                    />
                </g>
            </svg>

            {/* Trade Progress Bar */}
            {activePosition && (
                <TradeProgressBar
                    entryPrice={activePosition.entryPrice}
                    currentPrice={chartPrice}
                    positionType={activePosition.type}
                    leverage={activePosition.leverage}
                    liquidationPrice={liquidationPrice}
                    takeProfitPrice={positionLevels?.takeProfitPrice ?? null}
                />
            )}

            {/* Chart Overlays */}
            <div className="pointer-events-none absolute inset-0">
                {/* Top Left - Token Pair (mobile) / Engine info (desktop) */}
                <div className="absolute left-2 top-2 md:left-4 md:top-4">
                    {/* Mobile: Token pair */}
                    {tokenPair && (
                        <div className="flex items-center gap-1.5 md:hidden">
                            <div className="flex -space-x-1">
                                {/* Base coin icon (data-driven) */}
                                <TokenIcon
                                    token={baseToken}
                                    size="sm"
                                    className="z-10 border border-card"
                                />
                                {/* USDC quote icon */}
                                <TokenIcon
                                    token="usdc"
                                    size="sm"
                                    className="z-0 border border-card"
                                />
                            </div>
                            <span className="font-mono text-[10px] font-bold text-muted-foreground">
                                {tokenPair}
                            </span>
                        </div>
                    )}
                    {/* Desktop: live-feed indicator */}
                    <div className="hidden items-center gap-1.5 md:flex">
                        <span
                            className={cn(
                                'inline-block size-1.5 rounded-full',
                                connectionState === 'connected' && !isStale
                                    ? 'bg-primary'
                                    : 'bg-amber-400',
                            )}
                        />
                        <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                            {connectionState === 'connected' && !isStale
                                ? 'Live'
                                : connectionState === 'reconnecting'
                                  ? 'Reconnecting'
                                  : connectionState === 'connecting'
                                    ? 'Connecting'
                                    : isStale
                                      ? 'Stale'
                                      : 'Offline'}
                        </span>
                    </div>
                </div>

                {/* Top Right - Current Price (mobile only) */}
                {displayPrice !== undefined && (
                    <div className="absolute right-2 top-2 md:hidden">
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Price
                            </span>
                            <span
                                className={`font-mono text-lg font-bold tabular-nums tracking-tight text-foreground ${
                                    priceFlash === 'up'
                                        ? 'price-flash-up'
                                        : priceFlash === 'down'
                                          ? 'price-flash-down'
                                          : ''
                                }`}
                            >
                                {priceConfig.formatDisplay(displayPrice)}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Connection State Overlay */}
            {(connectionState === 'connecting' ||
                connectionState === 'reconnecting' ||
                connectionState === 'disconnected' ||
                isStale) && (
                <div
                    role="status"
                    className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm"
                >
                    <div className="flex flex-col items-center gap-2">
                        <div
                            className={cn(
                                'h-3 w-3 rounded-full',
                                connectionState === 'connecting' &&
                                    'animate-pulse bg-yellow-500',
                                connectionState === 'reconnecting' &&
                                    'animate-pulse bg-orange-500',
                                connectionState === 'disconnected' &&
                                    'bg-destructive',
                                isStale &&
                                    connectionState === 'connected' &&
                                    'bg-yellow-500',
                            )}
                        />
                        <span className="text-xs text-muted-foreground">
                            {connectionState === 'connecting' && 'Connecting…'}
                            {connectionState === 'reconnecting' &&
                                'Reconnecting…'}
                            {connectionState === 'disconnected' &&
                                'Connection lost'}
                            {isStale &&
                                connectionState === 'connected' &&
                                'Waiting for fresh prices…'}
                        </span>
                    </div>
                </div>
            )}

            {/* Dev Config Panel - only rendered in dev mode */}
            <DevConfigPanel
                currentPreset={devPreset}
                currentConfig={chartConfig}
                onPresetChange={setDevPreset}
                onConfigChange={(overrides) =>
                    setDevOverrides((prev) => deepMerge(prev, overrides))
                }
                onReset={handleDevReset}
            />
        </div>
    );
};

export default TrendChart;
